/**
 * Orders API: order history + order menu catalog.
 */
import { apiGet } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { getDefaultCurrencyCode } from '../../../shared/utils/currencyUtils';

export interface OrderItem {
  id?: number | string;
  name?: string;
  a_name?: string;
  title?: string;
  price?: number;
  quantity?: number;
  options?: string;
  attachment?: string;
  [key: string]: unknown;
}

export interface Order {
  id: number | string;
  status: string;
  payment_status?: string;
  date?: string;
  created_at?: string;
  total?: number | string;
  currency?: string;
  items?: OrderItem[];
  address?: { name?: string; address?: string; full_address?: string; phone?: string; [key: string]: unknown };
  delivery_fee?: number;
  discount?: number;
  payment_method?: string;
  area?: string;
  /** Set when order was placed with a coupon */
  coupon_code?: string;
  /** Amount redeemed from loyalty (same currency as org dialing profile). */
  redeem_from_loyalty?: number;
  [key: string]: unknown;
}

function formatOrderAddress(addr: any, area?: string): string {
  if (!addr || typeof addr !== 'object') {return '';}
  const parts = [
    area,
    addr.block,
    addr.street,
    addr.house_name,
    addr.avenue,
    addr.floor ? `Floor ${addr.floor}` : '',
    addr.flat ? `Flat ${addr.flat}` : '',
    addr.landmark,
  ].filter(Boolean);
  return parts.join(', ');
}

function normalizeOrder(raw: any): Order {
  const raw_items = Array.isArray(raw.menu_items)
    ? raw.menu_items
    : Array.isArray(raw.items)
      ? raw.items
      : Array.isArray(raw.order_items)
        ? raw.order_items
        : [];
  const items: OrderItem[] = raw_items.map((item: any) => ({
    id: item.id ?? item.menu_item_id,
    name: item.name ?? item.title ?? item.menu_item_name ?? '—',
    a_name: item.a_name,
    title: item.title ?? item.name,
    price: Number(item.price ?? item.unit_price ?? 0),
    quantity: Number(item.quantity ?? item.qty ?? 1),
    options: item.options,
    attachment: item.attachment,
  }));

  const addr = raw.address ?? raw.delivery_address;
  const full_address = formatOrderAddress(addr, raw.area);
  const address_normalized =
    addr && typeof addr === 'object'
      ? { ...addr, address: full_address || addr.address, full_address: full_address || addr.full_address }
      : undefined;

  return {
    id: raw.id ?? raw.order_id ?? 0,
    status: String(raw.status ?? raw.order_status ?? 'pending').toLowerCase(),
    payment_status: raw.payment_status != null ? String(raw.payment_status).toLowerCase() : undefined,
    date: raw.date ?? raw.created_at ?? raw.order_date ?? '',
    created_at: raw.created_at ?? raw.date,
    total: raw.cost != null ? raw.cost : (raw.total ?? raw.total_amount ?? 0),
    currency: raw.currency ?? getDefaultCurrencyCode(),
    items,
    address: address_normalized,
    delivery_fee: Number(raw.delivery_fee ?? raw.delivery_fee_amount ?? 0),
    discount: Number(raw.discount ?? 0),
    payment_method: raw.payment_method,
    area: raw.area,
    coupon_code: raw.coupon_code ?? raw.coupon ?? undefined,
    redeem_from_loyalty:
      Number(raw.redeem_from_loyalty ?? raw.loyalty_redeemed ?? 0) || undefined,
    ...raw,
  };
}

/**
 * Fetch orders for the current customer.
 */
export async function fetchOrders(customer_id: number): Promise<Order[]> {
  try {
    const res = await apiGet<any>(endpoints.orders, { customer_id: customer_id });
    const raw = res?.data?.orders ?? res?.data ?? res?.orders ?? res;
    const list = Array.isArray(raw) ? raw : [];
    return list.map(normalizeOrder);
  } catch {
    return [];
  }
}

export interface OrderMenuItem {
  id: number;
  name?: string;
  a_name?: string;
  description?: string;
  a_description?: string;
  price?: number;
  cost?: number;
  attachment?: string;
  image?: string;
  [key: string]: unknown;
}

export interface OrderMenuCategory {
  type_name?: string;
  a_type_name?: string;
  type_data?: OrderMenuItem[];
  items?: OrderMenuItem[];
}

export interface ShopSettings {
  minimum_order_cost?: number;
  [key: string]: unknown;
}

export interface OrderMenuResponse {
  menu_items?: OrderMenuCategory[];
  menuItems?: OrderMenuCategory[];
  configurations?: { shopSettings?: ShopSettings };
  shopSettings?: ShopSettings;
}

/**
 * Fetch order menu catalog (no date, no customer_id required for catalog).
 */
export async function fetchOrderMenuItems(): Promise<OrderMenuResponse> {
  const res = await apiGet<{ success?: boolean; data?: OrderMenuResponse } & OrderMenuResponse>(endpoints.menuItems);
  const data = (res as any).data ?? res;
  return {
    menu_items: data?.menu_items ?? data?.menuItems ?? [],
    configurations: data?.configurations,
    shopSettings: data?.shopSettings ?? data?.configurations?.shopSettings,
  };
}

/** Validate a coupon code (GET coupons/validate). */
export function validateCoupon(params: object) {
  return apiGet(endpoints.subscriptionCoupon, params);
}
